import { GarantieReferentielDto } from 'api/guarantee';
import { guaranteesAPI } from 'services/swagger-api';

/** Borne imposée par le back : au-delà, la requête est rejetée en validation. */
const MAX_PAGE_SIZE = 200;

/**
 * Garde-fou : au-delà, c'est que `totalCount` est aberrant. Mieux vaut une liste
 * tronquée qu'une rafale de requêtes.
 */
const MAX_PAGES = 25;

export interface GuaranteeFilters {
  branchId?: string | null;
  searchString?: string | null;
  unassignedOnly?: boolean;
}

/**
 * Récupère l'intégralité des garanties correspondant au filtre, page par page.
 *
 * Le référentiel dépasse la taille de page maximale : demander une grande page se
 * faisait rejeter, et se contenter de la première aurait faussé les compteurs par
 * branche comme la liste des garanties orphelines — sans que rien ne le signale à
 * l'écran. La première réponse donne le total, les pages restantes sont demandées
 * ensemble.
 */
export const fetchAllGuarantees = async (
  filters: GuaranteeFilters = {}
): Promise<{ items: GarantieReferentielDto[]; totalCount: number }> => {
  const requestPage = (pageNumber: number) =>
    guaranteesAPI.searchReferentialGuarantees({
      searchGarantiesReferentielCommand: {
        branchId: filters.branchId ?? undefined,
        searchString: filters.searchString ?? undefined,
        unassignedOnly: filters.unassignedOnly ?? false,
        pageNumber,
        pageSize: MAX_PAGE_SIZE,
      },
    });

  const first = await requestPage(1);
  const items = first.items ?? [];
  const totalCount = first.totalCount ?? items.length;

  const pageCount = Math.min(Math.ceil(totalCount / MAX_PAGE_SIZE), MAX_PAGES);
  if (pageCount <= 1 || items.length === 0) {
    return { items, totalCount };
  }

  const others = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) => requestPage(index + 2))
  );

  return {
    items: others.reduce((all, page) => all.concat(page.items ?? []), items),
    totalCount,
  };
};
